import React, { useEffect, useState } from "react";
import { Box, Container } from "@mui/material";
import { useNavigate, useLocation, useParams } from "react-router-dom";

// Import step components
import UploadVideosStep from "../components/onboarding/UploadVideosStep";
import ProcessingStep from "../components/onboarding/ProcessingStep";
import CreateTestsSuccessStep from "../components/onboarding/CreateTestsSuccessStep";
import OnboardingProgress from "../components/onboarding/OnboardingProgress";

// Test creation flow configuration - streamlined for existing users
const CREATE_TESTS_STEPS = {
  UPLOAD_VIDEOS: 1,
  PROCESSING: 2,
  SUCCESS: 3
};

const STEP_NAMES = {
  [CREATE_TESTS_STEPS.UPLOAD_VIDEOS]: "Upload Videos",
  [CREATE_TESTS_STEPS.PROCESSING]: "Processing",
  [CREATE_TESTS_STEPS.SUCCESS]: "Complete"
};

// No persistent state management needed - users always start fresh

export default function CreateTests() {
  const navigate = useNavigate();
  const location = useLocation();
  const { studioId } = useParams();
  const [currentStep, setCurrentStep] = useState(CREATE_TESTS_STEPS.UPLOAD_VIDEOS);
  const [uploadedVideos, setUploadedVideos] = useState([]);

  useEffect(() => {
    // Set create tests flag when component mounts
    localStorage.setItem('inCreateTests', 'true');
    
    // Always start from the beginning - no persistence needed
    setCurrentStep(CREATE_TESTS_STEPS.UPLOAD_VIDEOS);
    
    // Clean up when component unmounts
    return () => {
      localStorage.setItem('inCreateTests', 'false');
    };
  }, []);

  const advanceToNextStep = (data = null) => {
    // Store uploaded videos when coming from upload step
    if (currentStep === CREATE_TESTS_STEPS.UPLOAD_VIDEOS && data) {
      setUploadedVideos(data);
    }

    const nextStep = currentStep + 1;
    setCurrentStep(nextStep);
  };

  const goBackToPreviousStep = () => {
    const previousStep = currentStep - 1;
    if (previousStep >= CREATE_TESTS_STEPS.UPLOAD_VIDEOS) {
      setCurrentStep(previousStep);
    }
  };

  const handleComplete = () => {
    // Remove create tests flag when flow is complete
    localStorage.setItem('inCreateTests', 'false');
    
    // Navigate back to the pages view for this studio
    navigate(`/studio/${studioId}/pages`);
  };

  // Render current step
  const renderCurrentStep = () => {
    switch (currentStep) {
      case CREATE_TESTS_STEPS.UPLOAD_VIDEOS:
        return <UploadVideosStep onComplete={advanceToNextStep} />;
      
      case CREATE_TESTS_STEPS.PROCESSING:
        return <ProcessingStep onComplete={advanceToNextStep} onGoBack={goBackToPreviousStep} uploadedVideos={uploadedVideos} studioId={studioId} />;
      
      case CREATE_TESTS_STEPS.SUCCESS:
        return <CreateTestsSuccessStep onComplete={handleComplete} studioId={studioId} />;
      
      default:
        return <UploadVideosStep onComplete={advanceToNextStep} />;
    }
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        height: "100vh",
        maxHeight: "100vh",
        backgroundColor: "#121212",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        color: "#fff",
        position: "relative",
        overflow: "hidden",
      }}
    >
      <Container 
        maxWidth="lg"
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          height: "100vh",
          maxHeight: "100vh",
          overflow: "hidden",
          py: 4,
        }}
      >
        <Box
          sx={{
            width: "100%",
            maxWidth: "800px",
            mx: "auto",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            height: "100%",
            overflow: "hidden",
          }}
        >
          {renderCurrentStep()}
        </Box>
      </Container>
      
      {/* Progress tracker is hidden for clean Apple-style design */}
      <OnboardingProgress currentStep={currentStep} visible={false} />
    </Box>
  );
} 